import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { PixieTask } from './types';
import { PixieSection } from './PixieSection';

// --- TYPE DEFINITIONS ---

interface PixieHelperCardProps {
  title?: string;
  subtitle?: string;
  status?: string;
  processingTasks?: PixieTask[];
  queuedTasks?: PixieTask[];
  completedTasks?: PixieTask[];
  collapsible?: boolean;
  onTaskCancel?: (taskId: string) => void;
  onTaskAction?: (taskId: string) => void;
  onStatusAction?: (status: 'neutral' | 'primary') => void;
  className?: string;
}

// --- MAIN COMPONENT ---

const PixieHelperCard: React.FC<PixieHelperCardProps> = ({
  title = 'Pixie AI Helper',
  subtitle = 'Pixie AI focuses on tasks, so you can focus on patients',
  status = 'Completed',
  processingTasks = [],
  queuedTasks = [],
  completedTasks = [],
  collapsible = true,
  onTaskCancel,
  onTaskAction,
  onStatusAction,
  className = '',
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const totalTasks = processingTasks.length + queuedTasks.length + completedTasks.length;

  const handleToggle = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <div className={cn('bg-white rounded-lg shadow-sm border border-gray-200', className)}>
      <div
        className={cn('flex items-center p-4', collapsible && 'cursor-pointer')}
        onClick={handleToggle}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleToggle()}
        tabIndex={collapsible ? 0 : -1}
        role={collapsible ? 'button' : 'region'}
        aria-expanded={!isCollapsed}
      >
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
            <Badge className="bg-violet-200 text-violet-800 border-0 text-xs font-medium">
              {status}
            </Badge>
          </div>
          {subtitle && <p className="text-sm font-light text-violet-600 mt-1">{subtitle}</p>}
        </div>
        {collapsible && (
          <div className="flex-shrink-0">
            {isCollapsed ? <ChevronDown className="h-5 w-5 text-violet-600" /> : <ChevronUp className="h-5 w-5 text-violet-600" />}
          </div>
        )}
      </div>

      {!isCollapsed && (
        <div>
          {processingTasks.length > 0 && (
            <PixieSection
              title="Processing..."
              tasks={processingTasks}
              onTaskCancel={onTaskCancel}
              onTaskAction={onTaskAction}
            />
          )}
          {queuedTasks.length > 0 && (
            <PixieSection
              title="Queue"
              tasks={queuedTasks}
              onTaskCancel={onTaskCancel}
              onTaskAction={onTaskAction}
            />
          )}
          {completedTasks.length > 0 && (
            <PixieSection
              title="Completed by Pixie"
              tasks={completedTasks}
              onTaskCancel={onTaskCancel}
              onTaskAction={onTaskAction}
            />
          )}
          {totalTasks === 0 && (
            <div className="px-6 py-12 text-center">
              <p className="text-sm font-light text-gray-500 mb-4">
                No tasks currently being processed by Pixie AI
              </p>
              {onStatusAction && (
                <div className="flex gap-2 justify-center">
                  <Button variant="outline" size="sm" onClick={() => onStatusAction('neutral')}>Neutral</Button>
                  <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" onClick={() => onStatusAction('primary')}>Primary</Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

PixieHelperCard.displayName = 'PixieHelperCard';

export { PixieHelperCard };
export type { PixieHelperCardProps };
